/**
 * The office's names for an equipment row's status: the labels the equipment
 * register's pill shows, which every other office screen that names a status
 * should read from here rather than spell again.
 *
 * `left_on_site` IS "On Location" HERE, and the app currently has three
 * spellings for it. The register, equipment detail, the Alerts status badge and
 * the dashboard's deployment card say "On Location". The field screens, the
 * crew assignment screen and the job page say "Left on Site", and the Alerts
 * group heading says "Left On Site". Which of those is right is a product
 * decision, not a label fix. Until it is made, this map follows the register,
 * because it is the office's list of record for equipment.
 */

/** Every value of the equipment row's status. */
export type EquipmentStatus = 'available' | 'reserved' | 'in_use' | 'left_on_site' | 'maintenance' | 'retired';

export const EQUIPMENT_STATUS_LABELS: Record<EquipmentStatus, string> = {
  available: 'Available',
  reserved: 'Reserved',
  in_use: 'In Use',
  left_on_site: 'On Location',
  maintenance: 'In Maintenance',
  retired: 'Retired',
};

const KNOWN: ReadonlySet<string> = new Set(Object.keys(EQUIPMENT_STATUS_LABELS));

export function isEquipmentStatus(status: unknown): status is EquipmentStatus {
  return typeof status === 'string' && KNOWN.has(status);
}

/**
 * Label for a status off the wire. Same three outcomes as jobStatusLabel: a
 * known value gets its label, an absent one reads '—', and a value this build
 * has never heard of is echoed raw so somebody notices it.
 */
export function equipmentStatusLabel(status: unknown): string {
  if (isEquipmentStatus(status)) return EQUIPMENT_STATUS_LABELS[status];
  if (typeof status === 'string' && status.trim() !== '') return status;
  return '—';
}
